import { AppShell, useMantineColorScheme, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Outlet, ScrollRestoration } from "react-router";

import { CarburateLogo, Header } from "@/components/elements";
import BetaBanner from "@/components/elements/Banner/BetaBanner";
import HeaderDesktop from "@/components/elements/Header/HeaderDesktop";
import HeaderMobile from "@/components/elements/Header/HeaderMobile";
import SearchModal from "@/components/elements/SearchModal/SearchModal";
import { UserFeedback } from "@/components/widgets/UserFeedback";
import { APP_VERSION } from "@/config";
import { paths } from "@/config/paths";
import { useGarages } from "@/services/garages/get-garages";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { NavLinkItem } from "@/components/elements/Header/components";
import type { Garage } from "@/types/garage";
import type { FC } from "react";

export const DashboardContainer: FC = () => {
  const { t } = useTranslation();
  const { data: garages } = useGarages();
  const [opened, { open, close }] = useDisclosure(false);

  const { data: plans, isLoading: isPlanLoading } = usePlansUser();

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  /* Media Queries */
  const matches = useMatches({ md: true });
  const isDesktop = matches;

  /* Menu Links */
  const garageItem: NavLinkItem = getGarageNavLinkItem(garages?.results || []);
  function getGarageNavLinkItem(garages: Garage[]): NavLinkItem {
    const formerCarsItem = {
      label: t("header.formerCars"),
      to: paths.app.formerCars.root.getHref(),
    };

    if (garages.length >= 1) {
      return {
        label: t("header.garage"),
        to: paths.app.garage.root.getHref(),
        children: [
          ...garages.map((garage) => ({
            label: garage.data.name,
            to: paths.app.garage.root.getHref(garage.identifier),
          })),
          formerCarsItem,
        ],
      };
    }

    return {
      label: t("header.garage"),
      children: [formerCarsItem],
    };
  }

  return (
    <>
      <AppShell header={{ height: 60 }} withBorder={false} transitionDuration={0}>
        {/* Layout: Header */}
        <AppShell.Header>
          <BetaBanner />
          <Header
            leftSection={
              <CarburateLogo fill="white" h="18" title={`CARBURATE v${APP_VERSION}`} to="/" />
            }
            menu={[
              {
                end: true,
                label: t("header.dashboard"),
                to: paths.app.dashboard.getHref(),
              },
              garageItem,
              {
                label: t("header.market"),
                to: paths.app.markets.root.getHref(),
              },
              {
                label: t("header.auctions"),
                to: paths.app.auction.root.getHref(),
                hide: !plans?.config.auction_analytics || isPlanLoading,
              },
              {
                label: t("header.documents"),
                to: paths.app.documents.getHref(),
              },
            ]}
            rightSection={isDesktop ? <HeaderDesktop open={open} /> : <HeaderMobile open={open} />}
          />
        </AppShell.Header>

        {/* Layout: Main/Routing */}
        <AppShell.Main
          bg={isDark ? "var(--mantine-color-primaryDark-9)" : "inherit"}
          style={{ overflow: "hidden" }}
        >
          <Outlet />
        </AppShell.Main>
      </AppShell>

      {/* Common Components: Desktop/Mobile */}
      <ScrollRestoration />
      <SearchModal open={open} opened={opened} close={close} />

      <UserFeedback />
    </>
  );
};
